import React, { useState } from 'react';
import { BadgeCheckIcon, GlobeIcon, KeyRoundIcon, LockIcon, ShieldAlertIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input } from '../../components/ui/Field';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { CaseQueue } from '../../components/verification/CaseQueue';
import { useVerification } from '../../contexts/VerificationContext';
import { badgeDefinitions, verificationMetrics } from '../../data/verification';
import { LEVEL_LABEL, formatDate } from '../../utils/verification';
import type { DomainState } from '../../types/verification';
const TABS = ['Document cases', 'Institutions', 'Domain registry', 'Integrations', 'Policies', 'Badges', 'Analytics'] as const;
const DOMAIN_TONE: Record<DomainState, 'ok' | 'warn' | 'neutral' | 'danger'> = {
  approved: 'ok',
  pending_review: 'warn',
  suspended: 'neutral',
  rejected: 'danger'
};

/**
 * Academic verification administration. The registry surfaces here are the only
 * place a domain, integration or policy becomes trusted — nothing gains trust
 * because a user typed it or a screen assumed it.
 */
export function VerificationControl() {
  const {
    institutions,
    integrations,
    domains,
    policies,
    setDomainState
  } = useVerification();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Document cases');
  return <div className="space-y-6" data-id="element-8041" data-mp="2xdr06zymxf72">
      <PageHeader eyebrow="Academic identity" title="Verification control centre" description="Document review, the institution registry, approved email domains, institutional connections, access policies and badge definitions." data-id="element-8042" data-mp="2xdh733iamrx5" />

      <Panel data-id="element-8043" data-mp="29o64rhlaopgw">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-8044" data-mp="29nwo4km53unv">
          <Badge tone="warn" data-id="element-8045" data-mp="3czlpjf2s722i">Development data — not production metrics</Badge>
          <span className="text-body-sm text-ink-muted" data-id="element-8046" data-mp="38122fbcgbk8k">
            No surface in this console can set a verification level directly. A level only changes
            through a decided case or an institutional report.
          </span>
        </div>
      </Panel>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Verification administration sections" data-id="element-8047" data-mp="3hrn6heaw9gyv" />

      {tab === 'Document cases' && <PermissionGate permission="moderation.resolve" label="Reviewing academic document cases" data-id="element-8048" data-mp="2p2fyzptgph1h">
          <CaseQueue institutionId={null} reviewer="@you" mode="campuzon" data-id="element-8049" data-mp="331pfkqlkyd5z" />
        </PermissionGate>}

      {tab === 'Institutions' && <div className="space-y-4" data-id="element-8050" data-mp="2y1b40ykrsl37">
          <Panel title="Institutions are configuration" data-id="element-8051" data-mp="2y578vl6ow4r0">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8052" data-mp="iaf84ti4hii2">
              Every institution declares its own academic structure — what it calls a faculty, whether
              it uses year levels at all, what it calls a student identifier, and which statuses it
              recognises. Nothing here assumes a Nigerian university, a level system or a semester.
            </p>
          </Panel>
          {institutions.map(institution => <Panel key={institution.id} title={institution.name} description={`${institution.country} · ${institution.campuses.length} campus${institution.campuses.length === 1 ? '' : 'es'}`} action={<Badge plain data-id="element-8054" data-mp="19lb85txptc8s">Max: {LEVEL_LABEL[institution.maxLevelSupported]}</Badge>} data-id="element-8053" data-mp="gujizgf2hnuy">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-8055" data-mp="us83gn582szh">
                <div data-id="element-8056" data-mp="zs454fqg68bv">
                  <dt className="text-body-sm text-ink-muted" data-id="element-8057" data-mp="zrc9x60ricm4">Division is called</dt>
                  <dd className="text-body text-ink" data-id="element-8058" data-mp="1nhf2dn8l9kl9">{institution.structure.divisionLabel}</dd>
                </div>
                <div data-id="element-8059" data-mp="1sbt0vyijs7u5">
                  <dt className="text-body-sm text-ink-muted" data-id="element-8060" data-mp="3nwzcpehtk15">Year of study</dt>
                  <dd className="text-body text-ink" data-id="element-8061" data-mp="3lsr5v9k9yv2">
                    {institution.structure.levelLabel ?? 'Not used by this institution'}
                  </dd>
                </div>
                <div data-id="element-8062" data-mp="3qsv1ywiqpjvl">
                  <dt className="text-body-sm text-ink-muted" data-id="element-8063" data-mp="3lti4lhu8suer">Identifier is called</dt>
                  <dd className="text-body text-ink" data-id="element-8064" data-mp="3lvmfijqk6toj">{institution.structure.identifierLabel}</dd>
                </div>
                <div data-id="element-8065" data-mp="ictawwfrdf6i">
                  <dt className="text-body-sm text-ink-muted" data-id="element-8066" data-mp="icjfgtby2ftf">Statuses recognised</dt>
                  <dd className="cz-num text-body text-ink" data-id="element-8067" data-mp="dlt8xcrs86x5">
                    {institution.structure.statuses.length}
                  </dd>
                </div>
              </dl>
              <div className="mt-4 grid gap-4 lg:grid-cols-2" data-id="element-8068" data-mp="162dqr6gm7wbk">
                <div data-id="element-8069" data-mp="163ypz9dvk3fr">
                  <h3 className="text-body font-semibold text-ink" data-id="element-8070" data-mp="16471awucn0ou">Campuses</h3>
                  <ul className="mt-2 space-y-1.5" data-id="element-8071" data-mp="11acirwqhcrrc">
                    {institution.campuses.map(campus => <li key={campus.id} className="text-body-sm text-ink" data-id="element-8072" data-mp="18owk2cdbgzf7">
                        {campus.name} — {campus.city}
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-8073" data-mp="1njt3llqkt97l">
                  <h3 className="text-body font-semibold text-ink" data-id="element-8074" data-mp="1nkcjkwwo1niz">Accepted documents</h3>
                  {institution.acceptedDocuments.length === 0 ? <p className="mt-2 text-body-sm text-ink-muted" data-id="element-8075" data-mp="1rjtptsjx2op7">None configured.</p> : <ul className="mt-2 space-y-1.5" data-id="element-8076" data-mp="21bhgvscka3q5">
                      {institution.acceptedDocuments.map(entry => <li key={entry} className="text-body-sm text-ink" data-id="element-8077" data-mp="26au083484snz">
                          {entry}
                        </li>)}
                    </ul>}
                </div>
              </div>
            </Panel>)}
        </div>}

      {tab === 'Domain registry' && <PermissionGate permission="flags.edit" label="Managing approved institutional domains" data-id="element-8078" data-mp="g6qnukw2ba43">
          <div className="space-y-4" data-id="element-8079" data-mp="b98o9l3xq78r">
            <Panel title="A domain is never trusted because someone typed it" data-id="element-8080" data-mp="oykamp26ph24">
              <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8081" data-mp="p0nb630yqsl3">
                Each domain carries the evidence that established ownership, who added it and who
                reviewed it. A domain that merely resembles an institution's name is rejected and the
                rejection is kept, so it cannot quietly reappear.
              </p>
            </Panel>
            <Panel bodyClassName="p-0" data-id="element-8082" data-mp="17x4ym88s46bj">
              <ul className="divide-y divide-line" data-id="element-8083" data-mp="13w35m95ggqi0">
                {domains.map(domain => {
              const institution = institutions.find(entry => entry.id === domain.institutionId);
              return <li key={domain.id} className="px-5 py-4 sm:px-6" data-id="element-8084" data-mp="35o7xxlqv9pt9">
                      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-8085" data-mp="2vjhsv70625kt">
                        <div className="min-w-0" data-id="element-8086" data-mp="2zkuwdafsdwok">
                          <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-8087" data-mp="2zkl5lkmx3lht">
                            <GlobeIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-8088" data-mp="2ltg0w44p030m" />
                            {domain.domain}
                          </p>
                          <p className="text-body-sm text-ink-muted" data-id="element-8089" data-mp="2buh0f8u2zdop">
                            {institution?.name ?? 'Unknown institution'}
                          </p>
                          <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-8090" data-mp="22v3swlpioz6m">
                            Evidence: {domain.evidence}
                          </p>
                          <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-8091" data-mp="27w19wuysjn6j">
                            Added by {domain.addedBy} on {formatDate(domain.addedAt)} ·{' '}
                            {domain.reviewedBy ? `reviewed by ${domain.reviewedBy}` : 'not yet reviewed'}
                          </p>
                          {domain.note && <p className="mt-1 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-8092" data-mp="19oxs6buz1dc0">
                              {domain.note}
                            </p>}
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-8093" data-mp="10v2xxyve17b5">
                          <Badge tone={DOMAIN_TONE[domain.state]} data-id="element-8094" data-mp="vxawoe8us8ua">
                            {domain.state.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2" data-id="element-8095" data-mp="lyloxeao84rr">
                        {domain.state !== 'approved' && <Button variant="primary" size="sm" onClick={() => setDomainState(domain.id, 'approved', 'Ownership established and recorded.')} data-id="element-8096" data-mp="hz30i8jn5bk6">
                            Approve
                          </Button>}
                        {domain.state === 'approved' && <Button variant="secondary" size="sm" onClick={() => setDomainState(domain.id, 'suspended', 'Suspended pending re-confirmation. Existing verifications are untouched.')} data-id="element-8097" data-mp="3b9s4arpl1uva">
                            Suspend
                          </Button>}
                        {domain.state !== 'rejected' && <Button variant="danger" size="sm" onClick={() => setDomainState(domain.id, 'rejected', 'Not an institutional domain. Rejection kept on record.')} data-id="element-8098" data-mp="294ijto1is4hz">
                            Reject
                          </Button>}
                      </div>
                    </li>;
            })}
              </ul>
            </Panel>
            <Panel title="Suspending a domain" data-id="element-8099" data-mp="246qg7wriitxy">
              <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8100" data-mp="3bjirzihk2z4u">
                Suspending stops the domain being used for new verifications. It does not revoke
                verifications already granted through it — those are reviewed individually, because
                removing someone's standing without looking at their case would be the greater error.
              </p>
            </Panel>
          </div>
        </PermissionGate>}

      {tab === 'Integrations' && <PermissionGate permission="flags.edit" label="Viewing institutional connections" data-id="element-8101" data-mp="v33zdwozj7gw">
          <div className="space-y-4" data-id="element-8102" data-mp="q49ij0akvszf">
            <Panel title="What each connection can and cannot confirm" data-id="element-8103" data-mp="l7sl1fhho29o">
              <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8104" data-mp="l4wp1wp7bnnv">
                Institutions have very different technical infrastructure, so the connection model
                differs per institution. Each one states its capability explicitly — an institution
                without a connection cannot reach the top verification level, and the interface says so
                rather than offering it.
              </p>
            </Panel>
            {integrations.map(integration => {
          const institution = institutions.find(entry => entry.id === integration.institutionId);
          return <Panel key={integration.institutionId} title={institution?.name ?? 'Institution'} description={`${integration.model.replace('_', ' ')} · ${integration.provider}`} action={<Badge tone={integration.state === 'live' ? 'ok' : integration.state === 'failed' ? 'danger' : integration.state === 'none' ? 'neutral' : 'warn'} data-id="element-8106" data-mp="2gmg0iuvm9oa8">
                      {integration.state}
                    </Badge>} data-id="element-8105" data-mp="2phe18o50a5c4">
                  <div className="grid gap-5 lg:grid-cols-2" data-id="element-8107" data-mp="1o0ekute774c9">
                    <div data-id="element-8108" data-mp="1nxse2vz4ssv3">
                      <h3 className="text-body font-semibold text-ink" data-id="element-8109" data-mp="1gg400ukrkh4y">Can confirm</h3>
                      {integration.canConfirm.length === 0 ? <p className="mt-2 text-body-sm text-ink-muted" data-id="element-8110" data-mp="1bgrau36f6xz7">Nothing.</p> : <ul className="mt-2 space-y-1.5" data-id="element-8111" data-mp="1pey393slo5rh">
                          {integration.canConfirm.map(entry => <li key={entry} className="text-body-sm text-ink" data-id="element-8112" data-mp="wsd2xdu5cjbs">
                              {entry}
                            </li>)}
                        </ul>}
                    </div>
                    <div data-id="element-8113" data-mp="16jr4rs9klv83">
                      <h3 className="text-body font-semibold text-ink" data-id="element-8114" data-mp="16k0ynqxqkmj2">Cannot confirm</h3>
                      <ul className="mt-2 space-y-1.5" data-id="element-8115" data-mp="11pd69gzm37z7">
                        {integration.cannotConfirm.map(entry => <li key={entry} className="text-body-sm text-ink" data-id="element-8116" data-mp="93278arnlf4i">
                            {entry}
                          </li>)}
                      </ul>
                    </div>
                  </div>

                  <dl className="mt-4 grid gap-4 border-t border-line pt-4 sm:grid-cols-3" data-id="element-8117" data-mp="rpdw2b60rb5z">
                    <div data-id="element-8118" data-mp="wmx7l3bc19u8">
                      <dt className="text-body-sm text-ink-muted" data-id="element-8119" data-mp="wmdxu9vu5rra">Credentials</dt>
                      <dd className="flex flex-wrap items-center gap-2 text-body text-ink" data-id="element-8120" data-mp="11fyr5omhhxk5">
                        <KeyRoundIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-8121" data-mp="15f7k4r9p9ren" />
                        {integration.credentialState === 'held' ? 'Held securely' : integration.credentialState === 'rotation_due' ? 'Rotation due' : 'None held'}
                      </dd>
                      <dd className="mt-0.5 text-caption text-ink-subtle" data-id="element-8122" data-mp="3h6v12xlyv0oa">
                        Never rendered, never sent to a browser
                      </dd>
                    </div>
                    <div data-id="element-8123" data-mp="23ajwuw6hstwk">
                      <dt className="text-body-sm text-ink-muted" data-id="element-8124" data-mp="23atdht5ndopl">Last success</dt>
                      <dd className="text-body text-ink" data-id="element-8125" data-mp="1yi09j7i06kek">{formatDate(integration.lastSuccessAt)}</dd>
                    </div>
                    <div data-id="element-8126" data-mp="1uf3xjzmyxmjl">
                      <dt className="text-body-sm text-ink-muted" data-id="element-8127" data-mp="1pidhvdf2ssyp">Last failure</dt>
                      <dd className="text-body text-ink" data-id="element-8128" data-mp="1pgaja4zuxrid">{formatDate(integration.lastFailureAt)}</dd>
                    </div>
                  </dl>
                  <p className="mt-3 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-8129" data-mp="2i7u6dzzkrppn">{integration.note}</p>
                </Panel>;
        })}
            <Panel title="Institutional events" data-id="element-8130" data-mp="2d9ixpsl0nl90">
              <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8131" data-mp="287juqjt6uqcl">
                Where a connection can report changes, Campuzon accepts enrolment, status change,
                graduation, withdrawal, record update and revocation events. Each is signature-verified
                and idempotent, carries an event id, and a replayed event is a no-op rather than a
                second status change. Events that cannot be processed are held for inspection rather
                than dropped.
              </p>
            </Panel>
          </div>
        </PermissionGate>}

      {tab === 'Policies' && <div className="space-y-4" data-id="element-8132" data-mp="u7gn1hs2uber">
          <Panel title="Access policies" data-id="element-8133" data-mp="fk3zjniplvlq">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8134" data-mp="fj2m5ce80x0u">
              Every verification requirement in the product is a record here, not a condition written
              into a screen. Each names what it needs, and what someone who does not meet it can still
              do — a requirement without a fallback is a dead end.
            </p>
          </Panel>
          <Panel bodyClassName="p-0" data-id="element-8135" data-mp="3sqxcz7m5tp5a">
            <ul className="divide-y divide-line" data-id="element-8136" data-mp="3sryoteb02fo2">
              {policies.map(policy => <li key={policy.id} className="px-5 py-4 sm:px-6" data-id="element-8137" data-mp="1jb512ser0im">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-8138" data-mp="3e3oz1z1gv5pm">
                    <div className="min-w-0" data-id="element-8139" data-mp="3953vpqqx45wy">
                      <p className="text-body font-semibold text-ink" data-id="element-8140" data-mp="396exjl0sz3lf">{policy.feature}</p>
                      <p className="text-body-sm text-ink-muted" data-id="element-8141" data-mp="3o2w6y6pfm85c">{policy.surface}</p>
                      <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-8142" data-mp="3j205s0wgt03m">
                        Requires: {LEVEL_LABEL[policy.requiredLevel].toLowerCase()}
                        {policy.requiredStatuses && ` · status must be one of ${policy.requiredStatuses.length}`}
                        {policy.requiredInstitutionId && ' · institution-restricted'}
                      </p>
                      <p className="mt-0.5 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-8143" data-mp="5pg80velemzk">
                        Otherwise: {policy.fallback ?? 'Open to any account.'}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" data-id="element-8144" data-mp="fo5ul1hfovm5">
                      Edit
                    </Button>
                  </div>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Badges' && <div className="space-y-4" data-id="element-8145" data-mp="1vz6vcsqg7u23">
          <Panel title="Every badge states what it does not assert" data-id="element-8146" data-mp="1w087z1n44olx">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8147" data-mp="2000jnn7szavi">
              A bare tick is the failure mode here — it lets people read a mailbox check as an
              institutional confirmation. Each definition below names exactly what it establishes and
              what it does not. The generic “Verified” badge is kept inactive and visible so nobody
              reintroduces it.
            </p>
          </Panel>
          <Panel bodyClassName="p-0" data-id="element-8148" data-mp="1h3qwmaexb9zd">
            <ul className="divide-y divide-line" data-id="element-8149" data-mp="kk9saexfj4bq">
              {badgeDefinitions.map(definition => <li key={definition.id} className="px-5 py-4 sm:px-6" data-id="element-8150" data-mp="oln1myzqdpne">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-8151" data-mp="ojscf8hffbdi">
                    <div className="min-w-0" data-id="element-8152" data-mp="h09n6khlalwh">
                      <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-8153" data-mp="c313ugkpgbtn">
                        {definition.active ? <BadgeCheckIcon aria-hidden className="h-5 w-5 shrink-0 text-ok-700" data-id="element-8154" data-mp="83s8j7a0o5vz" /> : <ShieldAlertIcon aria-hidden className="h-5 w-5 shrink-0 text-warn-700" data-id="element-8155" data-mp="3u8m8p7wjlk6q" />}
                        {definition.label}
                      </p>
                      <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-8156" data-mp="3kit7n0u10ngq">
                        Asserts: {definition.asserts}
                      </p>
                      <p className="mt-0.5 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-8157" data-mp="3gj26q4ascqyh">
                        Does not assert: {definition.doesNotAssert}
                      </p>
                    </div>
                    <Badge tone={definition.active ? 'ok' : 'neutral'} data-id="element-8158" data-mp="36k38lfnncdpq">
                      {definition.active ? 'Active' : 'Retired — ambiguous'}
                    </Badge>
                  </div>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Analytics' && <div className="space-y-4" data-id="element-8159" data-mp="2qdpn60glxgs4">
          <Panel title="Verification analytics" data-id="element-8160" data-mp="2qehako01w87b">
            <p className="flex max-w-[80ch] items-start gap-2 text-body-sm text-ink" data-id="element-8161" data-mp="2qfsecnthxfyf">
              <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-8162" data-mp="2mdf71uz3p9zj" />
              Aggregate only. No individual's programme, identifier, documents or institutional email
              appears on this surface, whatever role you hold.
            </p>
          </Panel>
          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-8163" data-mp="27org62079ofx">
            {verificationMetrics.map(metric => <li key={metric.id} data-id="element-8164" data-mp="22q7tu0hp2an1">
                <Panel className="h-full" bodyClassName="flex h-full flex-col p-5" data-id="element-8165" data-mp="1ym017s848ajj">
                  <span className="cz-num text-h3 font-semibold text-ink" data-id="element-8166" data-mp="e4r4t1d00vfe">{metric.value}</span>
                  <span className="mt-1 text-body font-medium text-ink" data-id="element-8167" data-mp="i62kof62m2ae">{metric.label}</span>
                  <span className="mt-1 text-body-sm text-ink-muted" data-id="element-8168" data-mp="n55su8hckoml">{metric.basis}</span>
                </Panel>
              </li>)}
          </ul>
          <Panel title="Expiry and revalidation" data-id="element-8169" data-mp="s3qgz9jbrhuo">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8170" data-mp="3qm5t4n50buzs">
              No verification is permanent by default. Expiry is configured per institution and per
              method, students are told 30 days ahead, and a lapse removes the badge without touching
              the record or its history. Where an institutional connection exists, revalidation runs
              against it automatically; elsewhere it asks the student.
            </p>
          </Panel>
        </div>}
    </div>;
}